import { useCallback } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change/core/react/router';

import { useCampaignTracking } from 'src/app/shared/hooks/campaignTracking';
import { useCurrentUserAsync, useLoginState } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

import { usePetitionDetails } from '../../../../../hooks/petitionDetails';
import { useUserInfo } from '../../../../../hooks/userInfo';
import type { MediaHitEmbed } from '../../../shared/types';

import { useDeleteMediaModal } from './useDeleteMediaModal';

type Result = ModelHookResult<
	{ canDeleteMediaHit: boolean },
	{
		handleDeleteClick: () => Promise<void>;
	}
>;

export function useDeleteMediaHit(media: MediaHitEmbed, removeMedia: (mediaId: string) => void): Result {
	const { track } = useCampaignTracking();
	const { id: petitionId } = usePetitionDetails();

	const loginState = useLoginState();
	const location = useLocation();
	const navigate = useNavigate();
	const { ownPetition } = useUserInfo();

	const currentUser = useCurrentUserAsync();
	const user = isLoaded(currentUser) ? currentUser.value : undefined;
	const isCampaignOrSupportStaff = user?.roles.campaigns || user?.roles.support;

	const openModal = useDeleteMediaModal();

	const openDeleteModal = useCallback(
		async (mediaHit: MediaHitEmbed) => {
			const result = await openModal({
				petitionId,
				media: mediaHit,
			}).waitForResult();

			if (result?.value === 'success') {
				removeMedia(mediaHit.id);
			}
		},
		[openModal, petitionId, removeMedia],
	);

	const handleDeleteClick = useCallback(async () => {
		void track('campaign_media_hit_delete_button_click', {
			petition_id: petitionId,
			media_id: media.id,
			loginState,
		});
		if (loginState === 'IDENTIFIED') {
			navigate(`/login_or_join/redirected?redirectTo=${location.pathname}`);
		} else {
			await openDeleteModal(media);
		}
	}, [location, loginState, navigate, openDeleteModal, petitionId, track, media]);

	return {
		data: {
			canDeleteMediaHit: ownPetition || !!isCampaignOrSupportStaff,
		},
		actions: {
			handleDeleteClick,
		},
	};
}
